import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import {
  selectShipmentFilterState,
  setshipmentFilterState,
} from '@/redux-toolkit/slice/filterSlice.js';

import type {
  AppliedFilter,
  FilterArrayItem,
  ListItem,
  ShipmentFiltersPayload,
} from '../types';
import { getLegTypeFromSearch } from '../legTypeUtils';

type RegisterApplyFiltersFn = (
  fn: (args: {
    filterBy?: string;
    items?: ListItem[];
    append?: boolean;
    groupTitle?: string;
  }) => void
) => void;

type LegType = 'CL' | 'EL';

type ProgressFiltersByLeg = Partial<
  Record<
    LegType,
    {
      appliedFilters: AppliedFilter[];
      filterArray: FilterArrayItem[];
    }
  >
>;

export function useShipmentsFiltersController() {
  const dispatch = useDispatch();
  const location = useLocation();
  const filterStoreState = useSelector(selectShipmentFilterState);

  const [appliedFilters, setAppliedFilters] = useState<AppliedFilter[]>(
    (filterStoreState.appliedFilters as AppliedFilter[]) ?? []
  );

  const initialFilterArray: FilterArrayItem[] = useMemo(() => {
    return filterStoreState?.filterArray
      ? JSON.parse(JSON.stringify(filterStoreState.filterArray))
      : [];
  }, [filterStoreState?.filterArray]);

  const [filterArray, setFilterArray] =
    useState<FilterArrayItem[]>(initialFilterArray);

  const [isSearchVisible, setIsSearchVisible] = useState<boolean>(
    !!filterStoreState.isSearchVisible
  );

  const [searchQuery, setSearchQuery] = useState<string>(
    filterStoreState.searchQuery ?? ''
  );

  const [appliedSearchQuery, setAppliedSearchQuery] = useState<string>(
    filterStoreState.appliedSearchQuery ?? ''
  );

  const [activeViewType, setActiveViewType] = useState<string>(
    filterStoreState.activeViewType ?? 'list'
  );
  // A link from the User Activity feed carries the leg type of what it opens
  // (with a shipment_id): start on that CL/EL view instead of the stored one.
  const [activeLegType, setActiveLegType] = useState<'CL' | 'EL'>(() => {
    const fromActivity = Boolean(
      (location.state as { __zl_from_activity?: boolean } | null)
        ?.__zl_from_activity
    );
    const hasShipmentId = Boolean(
      new URLSearchParams(location.search).get('shipment_id')
    );
    const urlLegType =
      fromActivity && hasShipmentId
        ? getLegTypeFromSearch(location.search)
        : null;
    return urlLegType ?? filterStoreState.activeLegType ?? 'CL';
  });
  const [progressFiltersByLeg, setProgressFiltersByLeg] =
    useState<ProgressFiltersByLeg>(() => {
      return (
        (filterStoreState?.progressFiltersByLeg as ProgressFiltersByLeg) ?? {}
      );
    });

  const filtersPayloadRef = useRef<ShipmentFiltersPayload | null>(null);
  const setFiltersPayload = useCallback(
    (payload: ShipmentFiltersPayload | null) => {
      filtersPayloadRef.current = payload;
    },
    []
  );

  const panelApplyFiltersRef = useRef<
    | null
    | ((args: {
        filterBy?: string;
        items?: ListItem[];
        append?: boolean;
        groupTitle?: string;
      }) => void)
  >(null);

  const registerHandleApplyFilters: RegisterApplyFiltersFn = useCallback(
    (fn) => {
      panelApplyFiltersRef.current = fn;
    },
    []
  );

  const runPanelApplyFilters = useCallback(
    (args: {
      filterBy?: string;
      items?: ListItem[];
      append?: boolean;
      groupTitle?: string;
    }) => {
      panelApplyFiltersRef.current?.(args);
    },
    []
  );

  useEffect(() => {
    dispatch(
      setshipmentFilterState({
        appliedFilters,
        filterArray,
        searchQuery,
        isSearchVisible,
        appliedSearchQuery,
        activeViewType,
        activeLegType,
        progressFiltersByLeg,
      })
    );
  }, [
    dispatch,
    appliedFilters,
    filterArray,
    searchQuery,
    isSearchVisible,
    appliedSearchQuery,
    activeViewType,
    activeLegType,
    progressFiltersByLeg,
  ]);

  return {
    appliedFilters,
    filterArray,
    isSearchVisible,
    searchQuery,
    appliedSearchQuery,
    activeViewType,
    activeLegType,
    progressFiltersByLeg,

    setAppliedFilters,
    setFilterArray,
    setIsSearchVisible,
    setSearchQuery,
    setAppliedSearchQuery,
    setActiveViewType,
    setActiveLegType,
    setProgressFiltersByLeg,

    filtersPayloadRef,
    setFiltersPayload,

    registerHandleApplyFilters,
    runPanelApplyFilters,
  };
}
